'use client'

import {
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Mail,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

import type {
  Conversation,
} from '@/lib/inbox/inboxClient'

import {
  MessagesPopoverContent,
} from '@/components/inbox/MessagesPopoverContent'

import {
  InboxThreadDialog,
} from '@/components/inbox/InboxThreadDialog'

async function fetchConversations(
  userId: number
) {
  const res = await fetch(
    '/api/inbox/conversations',
    {
      method: 'GET',
      headers: {
        'x-user-id': String(userId),
      },
      cache: 'no-store',
    }
  )

  const json = await res.json()

  if (!res.ok || !json?.ok) {
    throw new Error(
      json?.error || 'API_ERROR'
    )
  }

  return (
    json.conversations || []
  ) as Conversation[]
}

export function MessagesHeaderButton({
  userId,
}: {
  userId: number | null
}) {
  const [convs, setConvs] =
    useState<Conversation[]>([])

  const [loading, setLoading] =
    useState(false)

  const [open, setOpen] =
    useState(false)

  const [
    selectedConversationId,
    setSelectedConversationId,
  ] = useState<number | null>(null)

  useEffect(() => {
    if (!userId) return

    let mounted = true

    async function load() {
      try {
        setLoading(true)

        const data =
          await fetchConversations(
            userId!
          )

        if (!mounted) return

        setConvs(data)
      } catch {
        if (!mounted) return
        setConvs([])
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    load()

    const t = setInterval(
      async () => {
        try {
          const data =
            await fetchConversations(
              userId
            )

          if (mounted) {
            setConvs(data)
          }
        } catch {}
      },
      15_000
    )

    return () => {
      mounted = false
      clearInterval(t)
    }
  }, [userId])

  useEffect(() => {
    if (!open || !userId) {
      return
    }

    ;(async () => {
      try {
        const data =
          await fetchConversations(
            userId
          )

        setConvs(data)
      } catch {}
    })()
  }, [open, userId])

  const unreadCount = useMemo(() => {
    if (!userId) return 0

    let count = 0

    for (const c of convs) {
      const last = Number(c.last_message_id || 0)
      const myRead = Number(c.my_last_read_message_id || 0)
      const lastSenderId = Number(c.last_sender_id || 0)

      const isUnread =
        last > myRead &&
        lastSenderId !== Number(userId)

      if (isUnread) {
        count++
      }
    }

    return count
  }, [convs, userId])

  const badgeText =
    unreadCount > 99
      ? '99+'
      : String(unreadCount)

  function handleOpenConversation(
    conversationId: number
  ) {
    setOpen(false)
    setSelectedConversationId(
      conversationId
    )
  }

  return (
    <>
      <div className="relative inline-flex">
        <Popover
          open={open}
          onOpenChange={setOpen}
        >
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="hubee-header-control relative !h-12 !w-12 !rounded-[16px] !p-0"
              title="Mensajes"
            >
              <Mail className="h-5 w-5" />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            align="end"
            side="bottom"
            sideOffset={10}
            className="
              z-[120]
              w-[380px]
              overflow-hidden
              rounded-2xl
              border
              bg-popover
              p-0
              shadow-xl
            "
          >
            {userId ? (
              <MessagesPopoverContent
                conversations={convs}
                userId={userId}
                onOpenConversation={
                  handleOpenConversation
                }
              />
            ) : null}
          </PopoverContent>
        </Popover>

        {unreadCount > 0 ? (
          <span className="
            pointer-events-none
            absolute
            -right-1
            -top-1
            z-10
            flex
            h-[18px]
            min-w-[18px]
            items-center
            justify-center
            rounded-full
            bg-red-500
            px-1
            text-[11px]
            font-semibold
            text-destructive-foreground
          ">
            {badgeText}
          </span>
        ) : null}

        {loading &&
        unreadCount === 0 ? (
          <span className="
            pointer-events-none
            absolute
            -right-1
            -top-1
            z-10
            h-2.5
            w-2.5
            rounded-full
            bg-muted-foreground/60
          " />
        ) : null}
      </div>
      <InboxThreadDialog
        conversationId={
          selectedConversationId
        }
        open={
          selectedConversationId !== null
        }
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setSelectedConversationId(null)
          }
        }}
      />
    </>
  )
}